import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, InputAdornment, TextField, useTheme } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { ViewTypes } from '../../../types/shell';
import FilterSelect from '../FilterSelect/FilterSelect';
import ViewSwitcher from '../ViewSwitcher/ViewSwitcher';

export interface IFilterOption {
  label: string;
  value: string;
}

export interface IFilterItem {
  label: string;
  tabId: string;
  value: string;
  onChange: (value: string) => void;
  options: IFilterOption[];
}

interface IProps {
  mobile: boolean;
  renderAddButton: React.JSX.Element | null;
  filterConfig: IFilterItem[];
  setSearchValue: (value: string) => void;
  activeView: ViewTypes;
  setActiveView: (view: ViewTypes) => void;
  handlePageChange: (page: number) => void;
}

const Filters = ({
  mobile,
  renderAddButton,
  filterConfig,
  handlePageChange,
  setSearchValue,
  activeView,
  setActiveView,
}: IProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const theme = useTheme();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
    setSearchValue(value);
  };

  const isAnyFilterActive = useMemo(() => {
    return filterConfig.some((filter) => filter.value.length > 0);
  }, [filterConfig]);

  const handleFilterChange = useCallback(
    (onChange: (value: string) => void, newValue: string) => {
      onChange(newValue);
      handlePageChange(1);
    },
    [handlePageChange],
  );

  const handleClearAllFilters = useCallback(() => {
    filterConfig.forEach((filter) => {
      filter.onChange('');
    });
  }, [filterConfig]);

  const handleClearFilter = useCallback((onChange: (value: string) => void) => {
    onChange('');
  }, []);

  const flexRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <TextField
          value={inputValue}
          onChange={handleSearchChange}
          placeholder="Search"
          variant="outlined"
          size="small"
          slotProps={{
            input: {
              sx: { borderRadius: '4px' },
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        {!mobile && renderAddButton}
      </Box>
      <Box sx={{ ...flexRowStyle, mt: 2.5 }}>
        <Box sx={flexRowStyle}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <FilterListIcon color="disabled" />
            {filterConfig.map(({ tabId, label, value, onChange, options }) => {
              return (
                <FilterSelect
                  key={tabId}
                  label={label}
                  value={value}
                  onChange={(newValue) => handleFilterChange(onChange, newValue)}
                  options={options}
                  onClear={handleClearFilter}
                />
              );
            })}
            {isAnyFilterActive && (
              <Button
                onClick={handleClearAllFilters}
                sx={{ fontSize: 16, fontWeight: theme.typography.fontWeightRegular, paddingY: 0, height: 32 }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </Box>

        <ViewSwitcher activeView={activeView} setActiveView={setActiveView} />
      </Box>
    </Box>
  );
};

export default Filters;
